import { useMemo, useState } from 'react';
import type React from 'react';
import type { AxiosError } from 'axios';
import { useNavigate } from 'react-router-dom';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from '@mui/material';
import { DataGrid, GridColDef, GridPaginationModel } from '@mui/x-data-grid';
import { ArrowLeft, Plus, Search, Trash2 } from 'lucide-react';
import { useSnackbar } from 'notistack';
import {
  useCreateSuppression,
  useDeleteSuppression,
  useSuppressions,
} from '../hooks/useNotificationCampaigns';
import type {
  AdminNotificationApiError,
  SuppressionCategory,
  SuppressionItem,
  SuppressionReason,
} from '../types';

const REASONS: SuppressionReason[] = [
  'unsubscribe',
  'bounce',
  'complaint',
  'manual',
  'provider_suppressed',
];
const CATEGORIES: SuppressionCategory[] = ['ALL', 'MARKETING', 'PRODUCT_UPDATE'];
const formatDate = (value?: string | null) => (value ? new Date(value).toLocaleString() : '-');

export const NotificationSuppressionsPage: React.FC = () => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const [search, setSearch] = useState('');
  const [reason, setReason] = useState<SuppressionReason | ''>('');
  const [pagination, setPagination] = useState<GridPaginationModel>({ page: 0, pageSize: 20 });
  const [addOpen, setAddOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<SuppressionItem | null>(null);
  const [email, setEmail] = useState('');
  const [category, setCategory] = useState<SuppressionCategory>('ALL');
  const [note, setNote] = useState('');
  const { data, isLoading, isFetching } = useSuppressions({
    search: search.trim() || undefined,
    reason: reason || undefined,
    page: pagination.page + 1,
    limit: pagination.pageSize,
  });
  const createMutation = useCreateSuppression();
  const deleteMutation = useDeleteSuppression();

  const columns = useMemo<GridColDef<SuppressionItem>[]>(
    () => [
      { field: 'email', headerName: 'Email', flex: 1, minWidth: 240 },
      { field: 'category', headerName: 'Category', width: 160 },
      { field: 'reason', headerName: 'Reason', width: 170 },
      { field: 'source', headerName: 'Source', width: 160 },
      {
        field: 'createdAt',
        headerName: 'Created',
        width: 190,
        valueFormatter: (value) => formatDate(value as string),
      },
      {
        field: 'actions',
        headerName: '',
        width: 90,
        sortable: false,
        renderCell: (params) => (
          <Button
            color="error"
            size="small"
            startIcon={<Trash2 size={16} />}
            onClick={(event) => {
              event.stopPropagation();
              setDeleteTarget(params.row);
            }}
          >
            Delete
          </Button>
        ),
      },
    ],
    []
  );

  const resetAddDialog = () => {
    setEmail('');
    setCategory('ALL');
    setNote('');
  };

  const handleCreate = async () => {
    try {
      await createMutation.mutateAsync({
        email: email.trim().toLowerCase(),
        category,
        note: note.trim() || undefined,
      });
      enqueueSnackbar('Suppression added.', { variant: 'success' });
      setAddOpen(false);
      resetAddDialog();
    } catch (error) {
      const response = (error as AxiosError<AdminNotificationApiError>).response?.data;
      enqueueSnackbar(
        response?.error === 'SUPPRESSION_EXISTS'
          ? 'This suppression already exists.'
          : response?.message || 'Unable to add suppression.',
        { variant: 'error' }
      );
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    await deleteMutation.mutateAsync(deleteTarget.publicId);
    enqueueSnackbar('Suppression deleted.', { variant: 'success' });
    setDeleteTarget(null);
  };

  return (
    <div className="min-h-screen min-w-0 bg-slate-50 p-4 dark:bg-slate-900 md:p-6">
      <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <Button
            className="mb-3"
            variant="text"
            startIcon={<ArrowLeft size={18} />}
            onClick={() => navigate('/admin-mail')}
          >
            Notifications
          </Button>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Suppression list</h1>
          <p className="mt-2 text-sm text-gray-600 dark:text-slate-400">
            Manage email addresses excluded from notification email delivery.
          </p>
        </div>
        <Button variant="contained" startIcon={<Plus size={18} />} onClick={() => setAddOpen(true)}>
          Add
        </Button>
      </div>

      <div className="mb-4 grid gap-3 md:grid-cols-[1fr_240px]">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
          <input
            value={search}
            onChange={(event) => {
              setSearch(event.target.value);
              setPagination((current) => ({ ...current, page: 0 }));
            }}
            placeholder="Search email..."
            className="h-11 w-full rounded-lg border border-gray-300 bg-white pl-10 pr-3 text-sm text-gray-900 focus:border-blue-500 focus:outline-none dark:border-slate-700 dark:bg-slate-800 dark:text-white"
          />
        </div>
        <select
          value={reason}
          onChange={(event) => {
            setReason(event.target.value as SuppressionReason | '');
            setPagination((current) => ({ ...current, page: 0 }));
          }}
          className="h-11 rounded-lg border border-gray-300 bg-white px-3 text-sm dark:border-slate-700 dark:bg-slate-800 dark:text-white"
        >
          <option value="">All reasons</option>
          {REASONS.map((item) => (
            <option key={item} value={item}>
              {item}
            </option>
          ))}
        </select>
      </div>

      <div className="mb-3 text-sm text-gray-500 dark:text-slate-400">
        {isFetching && !isLoading
          ? 'Refreshing suppressions...'
          : `${data?.total ?? 0} suppressions`}
      </div>
      <div className="rounded-lg bg-white shadow-sm dark:bg-slate-800" style={{ height: 650 }}>
        <DataGrid
          rows={data?.items || []}
          getRowId={(row) => row.publicId}
          columns={columns}
          rowCount={data?.total || 0}
          loading={isLoading}
          paginationMode="server"
          paginationModel={pagination}
          onPaginationModelChange={setPagination}
          pageSizeOptions={[10, 20, 50, 100]}
          disableRowSelectionOnClick
          sx={{ border: 'none' }}
        />
      </div>

      <Dialog open={addOpen} onClose={() => setAddOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Add suppression</DialogTitle>
        <DialogContent className="space-y-4">
          <TextField
            fullWidth
            type="email"
            label="Email"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
          />
          <FormControl fullWidth>
            <InputLabel>Category</InputLabel>
            <Select
              label="Category"
              value={category}
              onChange={(event) => setCategory(event.target.value as SuppressionCategory)}
            >
              {CATEGORIES.map((item) => (
                <MenuItem key={item} value={item}>
                  {item.replace('_', ' ')}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            fullWidth
            multiline
            minRows={3}
            label="Note"
            value={note}
            onChange={(event) => setNote(event.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            disabled={createMutation.isPending || !email.trim()}
            onClick={() => void handleCreate()}
          >
            Add
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(deleteTarget)}
        onClose={() => setDeleteTarget(null)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Delete suppression?</DialogTitle>
        <DialogContent>
          {deleteTarget
            ? `${deleteTarget.email} will be eligible for future email sends in this category.`
            : ''}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)}>Cancel</Button>
          <Button
            color="error"
            variant="contained"
            disabled={deleteMutation.isPending}
            onClick={() => void handleDelete()}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};
